import { UserSwitchOutlined } from "@ant-design/icons";
import { AutoComplete, Form, Menu, Modal, Select, Typography } from "antd";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { getAllCustomers } from "../../store/actions/customerActionCreators";
import { CustomerDto } from "../../store/actions/dtos/CustomerDto";
import { startImpersonation } from "../../store/actions/authActionCreators";
import { saveAuthUserInfo } from "./header";
import { FormInstance } from "antd/es/form/Form";
import ModalSaveCancelButtons from "../ModalSaveCancelButtons";
import Flex from "../Flex";

type ImpersonateFormValues = {
  customerId: string | null;
};

export function ImpersonateUserModal({
  modalIsOpenState: [modalIsOpen, setModalIsOpen],
}: {
  modalIsOpenState: [boolean, Dispatch<SetStateAction<boolean>>];
}) {
  const [impersonationIsLoading, setImpersonationIsLoading] =
    useState<boolean>(false);

  const [form] = Form.useForm<ImpersonateFormValues>();

  async function attemptSubmitForm(values: ImpersonateFormValues) {
    setImpersonationIsLoading(true);
    if (!values.customerId) {
      return;
    }
    await handleStartImpersonation(values.customerId);
    setImpersonationIsLoading(false);
  }

  async function handleStartImpersonation(customerId: string) {
    await startImpersonation(customerId).then(([impersonationRes, error]) => {
      if (!error) {
        setTimeout(() => {
          saveAuthUserInfo(impersonationRes);
          window.location.reload();
        }, 0);
      }
    });
  }

  function handleCloseModal() {
    form.resetFields();
    setModalIsOpen(false);
  }

  const selectedCustomerId =
    Form.useWatch("customerId", { preserve: true, form }) ?? null;

  return (
    <Modal
      visible={modalIsOpen}
      footer={null}
      centered
      maskClosable={false}
      keyboard={false}
      onCancel={handleCloseModal}
    >
      <Flex vertical gap="1rem">
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Log in as Customer
        </Typography.Title>
        <Form
          form={form}
          preserve
          layout="vertical"
          colon={false}
          name="poolCoverQuoteForm"
          initialValues={{ customerId: null }}
          onFinish={attemptSubmitForm}
        >
          <Flex vertical gap="1rem">
            <CustomersAutocomplete />
            <Typography.Text style={{ opacity: 0.7 }}>
              *Upon clicking "Log in as Customer", the page will refresh and you
              will be logged in as your specified customer.
            </Typography.Text>
          </Flex>
          <ModalSaveCancelButtons
            disableSave={!selectedCustomerId}
            saveButtonLoading={impersonationIsLoading}
            saveButtonText="Log in as Customer"
            saveButtonColor="danger"
            onCancel={handleCloseModal}
          />
        </Form>
      </Flex>
    </Modal>
  );
}

function CustomersAutocomplete() {
  const [customers, setCustomers] = useState<CustomerDto[] | null>(null);
  const [customersAreLoading, setCustomersAreLoading] =
    useState<boolean>(false);

  async function _getAllCustomers() {
    setCustomersAreLoading(true);
    try {
      const [customersRes, error] = await getAllCustomers();
      if (!!customersRes) {
        setCustomers(customersRes.data);
      }
    } catch (err) {
      console.error(err);
    }
    setCustomersAreLoading(false);
  }

  useEffect(() => {
    _getAllCustomers();
  }, []);

  const mappedOptions =
    customers?.map((user) => ({
      value: user.id,
      label: `${user.customerCode} - ${user.companyName} - ${user.email}`,
    })) ?? [];

  return (
    <Form.Item
      style={{ margin: 0 }}
      label="Customer"
      name="customerId"
      rules={[{ required: true }]}
    >
      <Select
        dropdownMatchSelectWidth={false}
        loading={customersAreLoading}
        clearIcon
        showSearch
        style={{ width: "100%" }}
        optionFilterProp="label"
        filterSort={(optionA, optionB) =>
          (optionA?.label ?? "")
            .toLowerCase()
            .localeCompare((optionB?.label ?? "").toLowerCase())
        }
        placeholder="Search for and or Select a customer to impersonate"
        options={mappedOptions}
      />
    </Form.Item>
  );
}
